import { createUser, type UserResult } from "./API/users";

export interface CurrentUser {
  userId: string;
  displayName: string;
}

const userIdCookieName = "dawndiver_user_id";
const displayNameCookieName = "dawndiver_display_name";
const cookieMaxAgeSeconds = 60 * 60 * 24 * 365;

let currentUserPromise: Promise<CurrentUser> | null = null;

export function getStoredCurrentUser(): CurrentUser | null {
  const userId = readCookie(userIdCookieName);

  if (!userId) {
    return null;
  }

  return {
    userId,
    displayName: readCookie(displayNameCookieName) || "Dawn Diver User",
  };
}

export function ensureCurrentUser() {
  const storedUser = getStoredCurrentUser();

  if (storedUser) {
    return Promise.resolve(storedUser);
  }

  currentUserPromise ??= createUser({
    displayName: createDisplayName(),
  }).then((user) => {
    const currentUser = toCurrentUser(user);
    saveCurrentUser(currentUser);
    return currentUser;
  });

  return currentUserPromise;
}

function toCurrentUser(user: UserResult): CurrentUser {
  return {
    userId: user.userId,
    displayName: user.displayName,
  };
}

function saveCurrentUser(user: CurrentUser) {
  writeCookie(userIdCookieName, user.userId);
  writeCookie(displayNameCookieName, user.displayName);
}

function createDisplayName() {
  return `Dawn Diver ${crypto.randomUUID().slice(0, 8)}`;
}

function readCookie(name: string) {
  if (typeof document === "undefined") {
    return "";
  }

  const encodedName = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split("; ")
    .find((value) => value.startsWith(encodedName));

  return cookie ? decodeURIComponent(cookie.slice(encodedName.length)) : "";
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value,
  )}; Path=/; Max-Age=${cookieMaxAgeSeconds}; SameSite=Lax`;
}
